import {
  Component,
  h,
  Element,
  Method,
  Prop,
  State,
  Listen,
  Event,
  EventEmitter,
} from '@stencil/core';
import { isBreakpointEqualOrSmaller } from '../../../utils';
import { FocusTrapHelper } from '../../../utils/functions/focusTrapHelper';
import { setScrollLock } from '../../../utils/functions/scrollLock';

@Component({
  tag: 'dc-ui-header-language',
  styleUrl: 'dc-ui-header-language.scss',
  shadow: true,
})
export class DcUiHeaderLanguage {
  @Element() el: HTMLElement;
  @Prop() headline?: string;
  @Prop() backButtonLabel?: string;

  @State() isMobile: boolean = false;

  private dialog: HTMLDialogElement;
  private isOpen: boolean = false;
  private focusTrapHelper: FocusTrapHelper;

  private readonly updateIsMobile = () => {
    this.isMobile = isBreakpointEqualOrSmaller('xl');
  };

  // Events
  @Event() dcFlyoutClose: EventEmitter<HTMLDcUiHeaderLanguageElement>;

  @Listen('click', { target: 'window' })
  onOutsideClick(e: MouseEvent) {
    if (!this.isOpen) return;

    // Get the full path of elements including through shadow DOM boundaries
    const path = e.composedPath();

    // Check if our element is in the path
    if (!path.includes(this.el) && this.isOpen) {
      this.close();
    }
  }

  @Listen('keydown', { target: 'window' })
  onKeyDown(e: KeyboardEvent) {
    if (!this.isOpen) return;

    if (e.key === 'Escape') {
      this.close();
    }
  }

  // method to open the modal
  @Method()
  async open() {
    setScrollLock(true);
    this.dialog.show();
    setTimeout(() => {
      this.isOpen = true;
    }, 100);
    this.focusTrapHelper.activateFocusTrap();
  }

  // method to close the modal
  @Method()
  async close() {
    if (!this.isMobile) {
      setScrollLock(false);
    }
    this.dialog.close();
    setTimeout(() => {
      this.isOpen = false;
    }, 100);
    this.focusTrapHelper.deactivateFocusTrap();
  }

  @Method()
  async toggle() {
    if (this.dialog.open) {
      this.close();
    } else {
      this.open();
    }
  }

  componentDidLoad() {
    this.updateIsMobile();
    window.addEventListener('resize', this.updateIsMobile);
    this.focusTrapHelper = new FocusTrapHelper(this.el);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.updateIsMobile);
  }

  private readonly iconClickHandler = () => {
    this.dcFlyoutClose.emit(this.el as HTMLDcUiHeaderLanguageElement);
    this.close();
  };

  render() {
    const backButton = (
      <button
        class="back-button"
        onClick={this.iconClickHandler}>
        <dc-ui-icon
          size="tiny"
          name="dc_backward_left"></dc-ui-icon>
        {this.backButtonLabel}
      </button>
    );

    const closeButton = (
      <dc-ui-button
        class="close-button"
        variant="link"
        icon="dc_close"
        hideLabel={true}
        id="close-icon"
        size="small"
        onClick={this.iconClickHandler}>
        Close Modal
      </dc-ui-button>
    );

    return (
      <dialog
        class="language-dialog"
        ref={(el) => (this.dialog = el)}>
        <div class="dialog-body">
          <div class="dialog-content">
            <div class="title-wrapper">
              {this.headline && (
                <dc-ui-text
                  variant="h3"
                  color="darkBlue02">
                  {this.headline}
                </dc-ui-text>
              )}
            </div>

            <div class="content-wrapper">
              <slot />
            </div>

            {this.isMobile ? (this.backButtonLabel ? backButton : closeButton) : closeButton}
          </div>
        </div>
      </dialog>
    );
  }
}
